/**
 * Grouping past conversations so a vendor can find one again.
 *
 * Kept apart from the menu that renders it because the interesting part is the
 * arithmetic — which bucket a conversation falls into, and how its age reads —
 * and that is worth testing without a DOM.
 */

export interface HistoryThread {
    uuid: string;
    title: string;
    lastMessageAt: string;
}

export interface HistoryGroup {
    /** i18n key for the heading. */
    key: string;
    /** English fallback, so the list still reads if a language lacks the key. */
    fallback: string;
    threads: HistoryThread[];
}

const DAY = 24 * 60 * 60 * 1000;

/**
 * How old, in the shorthand a list can afford.
 *
 * Minutes and hours for today, then days, then weeks. Anything a vendor has to
 * decode is worse than nothing here — this sits at the end of a row whose
 * subject is the conversation's title, not its age.
 */
export function ageLabel(at: string, now: Date = new Date()): string {
    const then = Date.parse(at);
    if (Number.isNaN(then)) return '';

    const elapsed = Math.max(0, now.getTime() - then);
    const minutes = Math.floor(elapsed / 60_000);
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;

    const days = Math.floor(elapsed / DAY);
    if (days < 7) return `${days}d`;

    return `${Math.floor(days / 7)}w`;
}

/**
 * Conversations in the order they will be read, under headings.
 *
 * Newest first within each group, and empty groups are dropped rather than
 * rendered as a heading with nothing under it.
 */
export function groupThreads(
    threads: HistoryThread[],
    now: Date = new Date(),
): HistoryGroup[] {
    const buckets: Array<HistoryGroup & { within: number }> = [
        { key: 'ASSISTANT.HISTORY.TODAY', fallback: 'Today', within: 1, threads: [] },
        { key: 'ASSISTANT.HISTORY.WEEK', fallback: 'Past week', within: 7, threads: [] },
        { key: 'ASSISTANT.HISTORY.MONTH', fallback: 'Past month', within: 30, threads: [] },
        { key: 'ASSISTANT.HISTORY.OLDER', fallback: 'Older', within: Infinity, threads: [] },
    ];

    const sorted = [...threads].sort(
        (a, b) => Date.parse(b.lastMessageAt) - Date.parse(a.lastMessageAt),
    );

    for (const thread of sorted) {
        const then = Date.parse(thread.lastMessageAt);
        // An unparseable date is still a conversation the vendor had. Filed as
        // old rather than dropped — losing a row is worse than misfiling one.
        const days = Number.isNaN(then) ? Infinity : Math.floor((now.getTime() - then) / DAY);
        (buckets.find((b) => days < b.within) ?? buckets[buckets.length - 1]).threads.push(thread);
    }

    return buckets
        .filter((b) => b.threads.length > 0)
        .map(({ key, fallback, threads: list }) => ({ key, fallback, threads: list }));
}

/**
 * The conversations a search term still names.
 *
 * Word-wise, because a vendor types what they remember saying rather than the
 * title we generated from it — "the oak rug" should still find "Three room
 * scenes of the oak rug".
 */
export function matchThreads(threads: HistoryThread[], query: string): HistoryThread[] {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return threads;
    return threads.filter((th) => {
        const title = (th.title || '').toLowerCase();
        return words.every((w) => title.includes(w));
    });
}
